import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { format, subDays } from 'date-fns';
import { FaHistory, FaSearch, FaListAlt } from 'react-icons/fa';
import { fetchActivityLogs } from '../../utils/activityLogApi';
import { showError } from '../../components/CustomAlert';

const DETAIL_LABELS = {
  studentId: 'Student ID',
  studentName: 'Student name',
  attendanceRecordId: 'Attendance record ID',
  attendanceDate: 'Attendance date',
  status: 'Status',
  class_joined_at: 'Class joined at',
  class_left_at: 'Class left at',
  leave_reason: 'Leave reason',
  notes: 'Notes',
  sessionId: 'Session ID',
  recorded_by: 'Recorded by (staff ID)',
  recordCount: 'Number of dates',
  firstDate: 'First date (sample)',
  firstStatus: 'First status (sample)',
  firstJoined: 'First joined time (sample)',
  firstLeft: 'First left time (sample)',
  courseId: 'Course ID',
  attendanceDateBulk: 'Class date',
  studentCount: 'Students in batch',
  sampleStudentId: 'Sample student ID',
  sampleStatus: 'Sample status',
  studentIds: 'Student IDs',
  studentsMoved: 'Students moved',
  reason: 'Reason',
  loginID: 'Login ID',
  firstName: 'First name',
  lastName: 'Last name',
  name: 'Name',
  email: 'Email',
  positionId: 'Position ID',
  privilegeCount: 'Permissions count',
  course: 'Course',
  session: 'Session',
  registrationNumber: 'Registration number',
  courseName: 'Course name',
  sessionName: 'Session name',
  courseCode: 'Course code',
  applicationId: 'Application ID',
  applicantName: 'Applicant name',
  sessionDate: 'Session date',
  sessionDocumentId: 'Document record ID',
  documentType: 'Document type',
  fileName: 'File name',
  sessionPreference: 'Session preference'
};

const ACTIVITY_LOG_FALLBACK_USER = 'Website visitor';
const ACTIVITY_LOG_FALLBACK_ROLE = 'Applicant';

/** Treat API/legacy placeholders as empty so fallbacks apply */
function isActivityPlaceholder(s) {
  if (s == null) return true;
  const t = String(s).trim();
  if (!t) return true;
  // Em dash, en dash, hyphen, underscore (backend or older clients)
  return /^[\u2014\u2013\-–_]+$/.test(t);
}

function displayActivityUserName(name) {
  const s = name == null ? '' : String(name).trim();
  if (isActivityPlaceholder(s)) return ACTIVITY_LOG_FALLBACK_USER;
  return s;
}

function displayActivityRole(role) {
  const s = role == null ? '' : String(role).trim();
  if (isActivityPlaceholder(s)) return ACTIVITY_LOG_FALLBACK_ROLE;
  return s;
}

function formatDetailKey(key) {
  return DETAIL_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDetailValue(val) {
  if (val == null) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

const Container = styled.div`
  padding: 30px;
  min-height: 100vh;
  background: #f8f9fa;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2c3e50;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 1rem;
  margin: 0;
  max-width: 720px;
  line-height: 1.5;
`;

const Card = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  align-items: end;
  width: 100%;
  box-sizing: border-box;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }
`;

const SearchButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 42px;
  &:hover {
    background: rgb(33, 41, 90);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Table = styled.table`
  /* index.css sets all tables to display:block — restore real table layout so columns span full width */
  display: table;
  width: 100%;
  min-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.95rem;
`;

const Th = styled.th`
  padding: 14px 12px;
  text-align: left;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  vertical-align: top;
`;

const Td = styled.td`
  padding: 14px 12px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
  vertical-align: top;
  line-height: 1.45;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const DetailButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid rgb(51, 66, 147);
  background: white;
  color: rgb(51, 66, 147);
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: rgba(51, 66, 147, 0.08);
  }
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageNav = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PageBtn = styled.button`
  padding: 8px 14px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: rgb(51, 66, 147);
    color: rgb(51, 66, 147);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #6c757d;
  line-height: 1.5;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 520px;
  width: 100%;
  max-height: 85vh;
  overflow: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: #2c3e50;
`;

const CloseBtn = styled.button`
  border: none;
  background: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #6c757d;
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 16px 20px 22px;
`;

const DetailDl = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(140px, 42%) 1fr;
  gap: 10px 16px;
  font-size: 0.92rem;
`;

const DetailDt = styled.dt`
  margin: 0;
  color: #6c757d;
  font-weight: 500;
`;

const DetailDd = styled.dd`
  margin: 0;
  color: #212529;
`;

const ActivityLog = () => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const weekAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');

  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);
  const [userSearch, setUserSearch] = useState('');
  const [keyword, setKeyword] = useState('');

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(null);

  const load = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        pageSize,
        from: from || undefined,
        to: to || undefined,
        userSearch: userSearch.trim() || undefined,
        keyword: keyword.trim() || undefined
      };
      const data = await fetchActivityLogs(params);
      setRows(data.rows || []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setPage(data.page || pageNum);
    } catch (e) {
      showError(e.message || 'Failed to load activity log');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [from, to, userSearch, keyword]);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPage(1);
    load(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const formatTs = (ts) => {
    if (!ts) return '—';
    try {
      return format(new Date(ts), 'MMM dd, yyyy h:mm a');
    } catch {
      return String(ts);
    }
  };

  const openDetails = (row) => {
    setDetailModal({
      title: row.actionPerformed || 'Activity details',
      subtitle: `${formatTs(row.created_at)} · ${displayActivityUserName(row.userDisplayName)}`,
      details: row.details && typeof row.details === 'object' ? row.details : null
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <FaHistory />
          Activity Log
        </Title>
        
      </Header>

      <Card>
        <FilterGrid>
          <FormGroup>
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>User</Label>
            <Input
              type="text"
              placeholder="Name or login (partial match)"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Action</Label>
            <Input
              type="text"
              placeholder="e.g. attendance, student, billing"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>&nbsp;</Label>
            <SearchButton type="button" onClick={handleSearch} disabled={loading}>
              <FaSearch /> Search
            </SearchButton>
          </FormGroup>
        </FilterGrid>
      </Card>

      <Card>
        {loading && rows.length === 0 ? (
          <EmptyState>Loading…</EmptyState>
        ) : !loading && rows.length === 0 ? (
          <EmptyState>
            No activity matches your filters. Try a wider date range or fewer search words. If this page is new,
            confirm the database migration for the activity log has been run on the server.
          </EmptyState>
        ) : (
          <>
            <TableWrap>
              <Table>
                <colgroup>
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '44%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <Th>Date &amp; time</Th>
                    <Th>User</Th>
                    <Th>Role</Th>
                    <Th>Action</Th>
                    <Th>Details</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Tr key={r.id}>
                      <Td>{formatTs(r.created_at)}</Td>
                      <Td>{displayActivityUserName(r.userDisplayName)}</Td>
                      <Td style={{ textTransform: 'capitalize' }}>{displayActivityRole(r.role)}</Td>
                      <Td>{r.actionPerformed || '—'}</Td>
                      <Td>
                        <DetailButton type="button" onClick={() => openDetails(r)}>
                          <FaListAlt /> Details
                        </DetailButton>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <PaginationRow>
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                {loading ? 'Loading…' : `Showing ${rows.length} of ${total} entries`}
              </span>
              <PageNav>
                <PageBtn type="button" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
                  Previous
                </PageBtn>
                <span style={{ fontSize: '0.9rem', color: '#495057' }}>
                  Page {page} / {totalPages}
                </span>
                <PageBtn type="button" disabled={page >= totalPages || loading} onClick={() => load(page + 1)}>
                  Next
                </PageBtn>
              </PageNav>
            </PaginationRow>
          </>
        )}
      </Card>

      {detailModal && (
        <ModalOverlay onClick={() => setDetailModal(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{detailModal.title}</ModalTitle>
              <CloseBtn type="button" onClick={() => setDetailModal(null)} aria-label="Close">
                ×
              </CloseBtn>
            </ModalHeader>
            <ModalBody>
              <p style={{ margin: '0 0 14px', color: '#6c757d', fontSize: '0.9rem' }}>{detailModal.subtitle}</p>
              {!detailModal.details || Object.keys(detailModal.details).length === 0 ? (
                <p style={{ margin: 0, color: '#6c757d' }}>
                  No extra fields were stored for this entry (older activity, or the action did not send a body).
                </p>
              ) : (
                <DetailDl>
                  {Object.entries(detailModal.details).map(([key, val]) => (
                    <React.Fragment key={key}>
                      <DetailDt>{formatDetailKey(key)}</DetailDt>
                      <DetailDd>{formatDetailValue(val)}</DetailDd>
                    </React.Fragment>
                  ))}
                </DetailDl>
              )}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ActivityLog;
